'use client'

import { useState, useEffect } from 'react'
import { useLocale } from 'next-intl'
import { X } from 'lucide-react'
import { Link } from '@/i18n/navigation'
import {
  isExternalAnnouncementLink,
  isValidAnnouncementLink,
  ANNOUNCEMENT_SEVERITY_COLORS,
  type AnnouncementSeverity,
} from '@/lib/announcements'

interface Announcement {
  id: string
  html_content: string
  height_percent: number
  /** Optional hex override for the banner background. NULL = severity/brand default. */
  background_color: string | null
  /** Optional hex override for the banner text color. NULL = default white. */
  text_color: string | null
  /** Optional click-through target. NULL = banner is not clickable. */
  link_url: string | null
  /** Drives default colors when no explicit hex override is set. */
  severity?: string
  /** When false, the viewer cannot dismiss it (no close button). */
  dismissible?: boolean
}

/**
 * Pick a readable text color (near-black vs white) for an arbitrary banner
 * background, so an admin-chosen `background_color` can never leave the copy
 * below WCAG AA (1.4.3). Uses the WCAG relative-luminance contrast formula.
 * Only consulted when no explicit text color is set; returns undefined for a
 * malformed hex so the caller falls back to the default white.
 */
function readableTextColor(bgHex: string): string | undefined {
  const h = bgHex.trim().replace(/^#/, '')
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return undefined
  const toLin = (v: number) => {
    const c = v / 255
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  }
  const r = toLin(parseInt(full.slice(0, 2), 16))
  const g = toLin(parseInt(full.slice(2, 4), 16))
  const b = toLin(parseInt(full.slice(4, 6), 16))
  const L = 0.2126 * r + 0.7152 * g + 0.0722 * b
  const contrastWhite = 1.05 / (L + 0.05)
  const contrastDark = (L + 0.05) / 0.05
  // Prefer whichever has more contrast; "dark" is gray-900, not pure black.
  return contrastDark >= contrastWhite ? '#111827' : '#ffffff'
}

/**
 * AnnouncementBanner — renders the active site announcement above the navigation.
 *
 * Fetches the active announcement on mount. Users can dismiss it for the session
 * via the close button (stored in sessionStorage so it reappears on next visit).
 *
 * When the announcement has a `link_url` the banner body becomes a
 * click-through. The API only serves rows targeted at this website
 * (platform 'desktop' or 'both') — mobile-only rows are the app's.
 */
export function AnnouncementBanner({
  initialAnnouncement,
}: {
  /** Server-rendered active announcement (or null). When provided — even as
   *  null — the banner renders at its final height on first paint and skips the
   *  on-mount fetch, so it no longer pops in and shift the page (CLS).
   *  `undefined` preserves the legacy fetch-on-mount behaviour. */
  initialAnnouncement?: Announcement | null
} = {}) {
  const locale = useLocale()
  const [announcement, setAnnouncement] = useState<Announcement | null>(
    initialAnnouncement ?? null,
  )
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    // Check if already dismissed this session. sessionStorage access can throw
    // a SecurityError when site data is blocked (e.g. Firefox tracking
    // protection / private mode) — swallow it and treat as "not dismissed".
    let dismissedId: string | null = null
    try {
      dismissedId = sessionStorage.getItem('dismissed_announcement')
    } catch {
      /* storage unavailable — treat as not dismissed */
    }

    // Server-rendered path: the banner is already in the SSR HTML at its final
    // height, so we only apply a stored dismissal here (no fetch → no CLS).
    if (initialAnnouncement !== undefined) {
      if (
        initialAnnouncement &&
        dismissedId === initialAnnouncement.id &&
        initialAnnouncement.dismissible !== false
      ) {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- sessionStorage is client-only; applying a stored dismissal in render would mismatch the SSR banner
        setDismissed(true)
      }
      return
    }

    // Legacy path (no server value passed): fetch the active announcement.
    // Pass the viewer's UI locale so locale-targeted announcements can filter.
    fetch(`/api/announcements/active?locale=${encodeURIComponent(locale)}`)
      .then((res) => res.json())
      .then((data) => {
        if (data?.id) {
          // Only honor a stored dismissal for a still-dismissible row — a row
          // flipped to non-dismissible must reappear.
          if (dismissedId === data.id && data.dismissible !== false) {
            setDismissed(true)
          }
          setAnnouncement(data)
        }
      })
      .catch(() => {
        // Silently fail — no banner is fine
      })
  }, [locale, initialAnnouncement])

  if (!announcement || dismissed) return null

  const handleDismiss = () => {
    // Persist the dismissal for the session; if storage is blocked the dismiss
    // still works for this render, it just won't survive a reload.
    try {
      sessionStorage.setItem('dismissed_announcement', announcement.id)
    } catch {
      /* storage unavailable — dismissal won't persist, which is fine */
    }
    setDismissed(true)
  }

  // Color precedence: explicit hex override → severity default → brand class.
  // An explicit `background_color`/`text_color` always wins. Otherwise a
  // non-'info' severity supplies its default palette. 'info' with no override
  // falls through to the `bg-brand-700 text-white` classes (unchanged — this
  // is what every pre-targeting row is), so existing banners look identical.
  const customBg = announcement.background_color
  const customText = announcement.text_color
  const severity = (announcement.severity ?? 'info') as AnnouncementSeverity
  const severityColors =
    severity !== 'info' ? ANNOUNCEMENT_SEVERITY_COLORS[severity] : undefined
  const effectiveBg = customBg ?? severityColors?.bg
  // When neither an explicit text color nor a severity default applies, derive
  // a readable one from the chosen background so a custom `background_color`
  // can't render white-on-light (WCAG 1.4.3). No bg override → keep the
  // `text-white` class on the brand-700 default (unchanged).
  const effectiveText =
    customText ??
    severityColors?.text ??
    (effectiveBg ? readableTextColor(effectiveBg) : undefined)
  const bannerStyle: React.CSSProperties = {
    minHeight: `${announcement.height_percent}vh`,
    ...(effectiveBg ? { backgroundColor: effectiveBg } : {}),
    ...(effectiveText ? { color: effectiveText } : {}),
  }

  // Re-validate at render time rather than trusting whatever is in the
  // row — the API rejects bad values on write, but a row seeded by hand
  // must never turn into a `javascript:` href.
  const rawLink = announcement.link_url?.trim() ?? ''
  const link = rawLink && isValidAnnouncementLink(rawLink) ? rawLink : null
  const external = link ? isExternalAnnouncementLink(link) : false

  const body = (
    // flex-col + text-center so multi-line / multi-block announcement HTML
    // stacks vertically and centers. (The default flex-row laid multiple
    // block children out side-by-side, and block content can't be centered
    // by align-items alone — text-center handles it regardless of the
    // author's inline styles.)
    <div
      className="flex flex-col items-center justify-center w-full h-full px-10 py-2 text-center"
      style={{ minHeight: `${announcement.height_percent}vh` }}
      dangerouslySetInnerHTML={{ __html: announcement.html_content }}
    />
  )

  return (
    <div
      className="relative w-full overflow-hidden bg-brand-700 text-white"
      style={bannerStyle}
    >
      {/* The dismiss button is a SIBLING of the link, not a child — a
          <button> inside an <a> is invalid HTML and would navigate on
          dismiss. Absolutely positioned above the anchor (z-10), it
          swallows its own click and the anchor never sees it, so no
          stopPropagation is needed. */}
      {link ? (
        external ? (
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full"
          >
            {body}
          </a>
        ) : (
          // Internal path — locale-aware Link keeps the visitor's
          // /es, /ja, … prefix instead of resetting them to English.
          <Link href={link} className="block w-full">
            {body}
          </Link>
        )
      ) : (
        body
      )}
      {announcement.dismissible !== false && (
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-3 z-10 p-1 opacity-70 hover:opacity-100 hover:bg-white/10 transition-opacity"
          style={effectiveText ? { color: effectiveText } : undefined}
          aria-label="Dismiss announcement"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  )
}
